import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Typography,
  Button,
  Stack,
  TextField,
  Switch,
  FormControlLabel,
  LinearProgress,
  IconButton,
  Stepper,
  Step,
  StepLabel,
  Checkbox,
} from "@mui/material";
import {
  CloudUpload as UploadIcon,
  Delete as DeleteIcon,
  MusicNote as MusicIcon,
  ArrowUpward as ArrowUpIcon,
  ArrowDownward as ArrowDownIcon,
  CheckCircle as CheckIcon,
  Error as ErrorIcon,
} from "@mui/icons-material";
import { useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import ApiService from "../../services/api-service";
import notifier from "../../components/Notifier";
import DropDown from "../../components/DropDown";
import { getBalance } from "../../asyncAction/asyncBalance";
import { checkExtension } from "../../constants/function";
import "./index.scss";

const steps = ["Album Details", "Upload Tracks", "Royalties", "Review & Submit"];

export default function UploadAlbum() {
  const history = useHistory();
  const dispatch = useDispatch();
  const { t } = useTranslation("common");
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [genres, setGenres] = useState([]);

  // Album details
  const [albumData, setAlbumData] = useState({
    title: "",
    description: "",
    genre: "",
    subGenre: "",
    releaseDate: new Date().toISOString().split("T")[0],
    isExplicit: false,
  });

  // Cover art
  const [coverFile, setCoverFile] = useState(null);
  const [coverPreview, setCoverPreview] = useState(null);

  // Tracks
  const [tracks, setTracks] = useState([]);
  const [dragOver, setDragOver] = useState(false);

  // Royalties — per-track splits
  const [usePerTrackSplits, setUsePerTrackSplits] = useState(false);
  const [defaultRoyalty, setDefaultRoyalty] = useState({
    ownerRoyalty: 100,
    collaborators: [],
  });
  // trackRoyalties keyed by track.id: { ownerRoyalty, collaborators: [{ name, percentage }] }
  const [trackRoyalties, setTrackRoyalties] = useState({});
  const [selectedTrackId, setSelectedTrackId] = useState(null);

  // Upload limits
  const [uploadLimits, setUploadLimits] = useState(null);

  // Rights confirmation
  const [rightsConfirmed, setRightsConfirmed] = useState(false);

  // Upload progress
  const [uploadProgress, setUploadProgress] = useState(0);
  const [trackStatuses, setTrackStatuses] = useState({});

  useEffect(() => {
    fetchGenres();
    fetchUploadLimits();
  }, []);

  const fetchUploadLimits = async () => {
    try {
      const response = await ApiService.GET_UPLOAD_LIMITS();
      setUploadLimits(response?.result || null);
    } catch (error) {
      console.error("Failed to fetch upload limits:", error);
    }
  };

  const fetchGenres = async () => {
    try {
      const response = await ApiService.GENRE_LIST();
      setGenres([...response["result"]]);
    } catch (error) {
      console.error("Failed to fetch genres:", error);
    }
  };

  // Cover art handler
  const handleCoverChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      notifier.error("Please select an image file");
      return;
    }
    if (file.size > 6 * 1024 * 1024) {
      notifier.error("Cover image must be under 6MB");
      return;
    }
    setCoverFile(file);
    const reader = new FileReader();
    reader.onloadend = () => setCoverPreview(reader.result);
    reader.readAsDataURL(file);
  };

  // Track handlers
  const processFiles = useCallback((files) => {
    const audioFiles = Array.from(files).filter(
      (file) =>
        file.type.startsWith("audio/") ||
        file.name.match(/\.(mp3|wav|m4a|flac)$/i)
    );
    if (audioFiles.length !== files.length) {
      notifier.warning("Some non-audio files were filtered out");
    }
    const newTracks = audioFiles.map((file, index) => ({
      id: Date.now() + index,
      file,
      name: file.name
        .replace(/\.[^/.]+$/, "")
        .replace(/^\d+[-_.\s]+/, ""),
      size: file.size,
    }));
    setTracks((prev) => [...prev, ...newTracks]);
  }, []);

  const handleTracksChange = (e) => {
    processFiles(e.target.files);
  };

  const handleDrop = useCallback(
    (e) => {
      e.preventDefault();
      setDragOver(false);
      processFiles(e.dataTransfer.files);
    },
    [processFiles]
  );

  const handleDragOver = (e) => {
    e.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = () => setDragOver(false);

  const removeTrack = (id) => {
    setTracks((prev) => prev.filter((t) => t.id !== id));
  };

  const renameTrack = (id, newName) => {
    setTracks((prev) =>
      prev.map((t) => (t.id === id ? { ...t, name: newName } : t))
    );
  };

  const moveTrack = (index, direction) => {
    const newTracks = [...tracks];
    const target = index + direction;
    if (target < 0 || target >= newTracks.length) return;
    [newTracks[index], newTracks[target]] = [newTracks[target], newTracks[index]];
    setTracks(newTracks);
  };

  // Royalty helpers — works for both default and per-track
  const getRoyaltyForTrack = (trackId) => {
    if (usePerTrackSplits && trackRoyalties[trackId]) {
      return trackRoyalties[trackId];
    }
    return defaultRoyalty;
  };

  const updateRoyalty = (royaltyObj, setRoyaltyFn) => ({
    setOwner: (val) =>
      setRoyaltyFn({ ...royaltyObj, ownerRoyalty: Math.min(100, Math.max(0, Number(val))) }),
    addCollab: () =>
      setRoyaltyFn({
        ...royaltyObj,
        collaborators: [...royaltyObj.collaborators, { name: "", percentage: 5 }],
      }),
    updateCollab: (index, field, value) => {
      const updated = [...royaltyObj.collaborators];
      updated[index] = { ...updated[index], [field]: value };
      setRoyaltyFn({ ...royaltyObj, collaborators: updated });
    },
    removeCollab: (index) =>
      setRoyaltyFn({
        ...royaltyObj,
        collaborators: royaltyObj.collaborators.filter((_, i) => i !== index),
      }),
    total: () => {
      const collabTotal = royaltyObj.collaborators.reduce(
        (sum, c) => sum + (Number(c.percentage) || 0),
        0
      );
      return Number(royaltyObj.ownerRoyalty) + collabTotal;
    },
  });

  const setTrackRoyalty = (trackId, royalty) => {
    setTrackRoyalties((prev) => ({ ...prev, [trackId]: royalty }));
  };

  // When toggling to per-track, initialize each track with the default splits
  const handleTogglePerTrack = (enabled) => {
    setUsePerTrackSplits(enabled);
    if (enabled) {
      const initial = {};
      tracks.forEach((t) => {
        if (!trackRoyalties[t.id]) {
          initial[t.id] = { ...defaultRoyalty, collaborators: [...defaultRoyalty.collaborators] };
        }
      });
      setTrackRoyalties((prev) => ({ ...initial, ...prev }));
      if (tracks.length > 0 && !selectedTrackId) {
        setSelectedTrackId(tracks[0].id);
      }
    }
  };

  // Validation
  const validateStep = (step) => {
    switch (step) {
      case 0:
        return (
          albumData.title &&
          albumData.genre &&
          albumData.subGenre &&
          albumData.description &&
          albumData.releaseDate &&
          coverFile
        );
      case 1:
        return tracks.length > 0;
      case 2: {
        if (usePerTrackSplits) {
          return tracks.every((t) => {
            const r = getRoyaltyForTrack(t.id);
            return updateRoyalty(r, () => {}).total() <= 100;
          });
        }
        return updateRoyalty(defaultRoyalty, () => {}).total() <= 100;
      }
      default:
        return true;
    }
  };

  const handleNext = () => {
    if (!validateStep(activeStep)) {
      if (activeStep === 0) {
        if (!albumData.title) notifier.error("Please enter an album title");
        else if (!coverFile) notifier.error("Please upload a cover image");
        else if (!albumData.genre) notifier.error("Please select a genre");
        else if (!albumData.subGenre) notifier.error("Please select a sub-genre");
        else if (!albumData.description) notifier.error("Please enter a description");
        else if (!albumData.releaseDate) notifier.error("Please select a release date");
      } else if (activeStep === 1) {
        notifier.error("Please add at least one track");
      } else if (activeStep === 2) {
        notifier.error("Total royalties cannot exceed 100%");
      }
      return;
    }
    setActiveStep((prev) => prev + 1);
  };

  const handleBack = () => setActiveStep((prev) => prev - 1);

  const formatFileSize = (bytes) => {
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  // Submit
  const handleSubmit = async () => {
    if (!rightsConfirmed) {
      notifier.error("You must confirm rights ownership before uploading.");
      return;
    }

    // Pre-check upload limits before starting
    if (uploadLimits && uploadLimits.remaining !== -1) {
      if (uploadLimits.remaining === 0) {
        notifier.error(
          uploadLimits.tier === "free"
            ? "You have reached your free upload limit of 3 songs. Subscribe to upload unlimited songs."
            : `You've reached your monthly upload limit of ${uploadLimits.limit} songs. Upgrade to Pro for unlimited uploads!`
        );
        return;
      }
      if (tracks.length > uploadLimits.remaining) {
        notifier.error(
          `You can only upload ${uploadLimits.remaining} more song${uploadLimits.remaining !== 1 ? "s" : ""}. ` +
          `This album has ${tracks.length} tracks. ` +
          (uploadLimits.tier === "free"
            ? "Subscribe to upload unlimited songs."
            : "Upgrade to Pro for unlimited uploads!")
        );
        return;
      }
    }

    setLoading(true);
    setUploadProgress(0);
    setTrackStatuses({});

    try {
      const totalTracks = tracks.length;
      let uploadedCount = 0;
      const albumGroupId = `album_${Date.now()}_${Math.random()
        .toString(36)
        .substr(2, 9)}`;

      for (let i = 0; i < tracks.length; i++) {
        const track = tracks[i];

        try {
          setTrackStatuses((prev) => ({
            ...prev,
            [track.id]: { status: "uploading", message: "Uploading..." },
          }));

          const formData = new FormData();
          formData.append("title", track.name);
          formData.append("genre", albumData.genre);
          formData.append("subGenre", albumData.subGenre);
          formData.append("releaseDate", albumData.releaseDate);
          formData.append("isExplicit", albumData.isExplicit);
          formData.append("description", albumData.description);
          formData.append("private", false);

          // Album grouping
          formData.append("releaseType", "albums");
          formData.append("albumTitle", albumData.title);
          formData.append("albumGroupId", albumGroupId);
          formData.append("trackNumber", i + 1);

          // Audio file
          formData.append("file", track.file);

          // Cover art
          if (coverFile) {
            const ext = checkExtension(coverFile.name);
            formData.append(
              "thumbnailImg",
              coverFile,
              `${new Date().getTime()}_${i}.${ext}`
            );
          }

          // Royalties — per-track or default
          const royalty = getRoyaltyForTrack(track.id);
          formData.append("ownerRoyalty", royalty.ownerRoyalty);
          if (royalty.collaborators.length > 0) {
            formData.append("collaborators", JSON.stringify(royalty.collaborators));
          }

          // NFT defaults
          formData.append("currency", "USD");
          formData.append("count", 10);
          formData.append("platform", "Siki");

          await ApiService.CREATE_MUSIC_NFT_V5(formData);

          uploadedCount++;
          setUploadProgress((uploadedCount / totalTracks) * 100);
          setTrackStatuses((prev) => ({
            ...prev,
            [track.id]: { status: "success", message: "Uploaded" },
          }));
        } catch (error) {
          console.error(`Failed to upload track ${track.name}:`, error);
          setTrackStatuses((prev) => ({
            ...prev,
            [track.id]: {
              status: "error",
              message: error?.message || "Upload failed",
            },
          }));
        }
      }

      dispatch(getBalance());
      notifier.success(
        `Album uploaded! ${uploadedCount}/${totalTracks} tracks successful.`
      );

      setTimeout(() => {
        history.push("/app/Library");
      }, 1500);
    } catch (error) {
      console.error("Album upload failed:", error);
      notifier.error("Failed to upload album");
    } finally {
      setLoading(false);
    }
  };

  // Render steps
  const renderStep = () => {
    switch (activeStep) {
      case 0:
        return renderAlbumDetails();
      case 1:
        return renderUploadTracks();
      case 2:
        return renderRoyalties();
      case 3:
        return renderReview();
      default:
        return null;
    }
  };

  const renderAlbumDetails = () => (
    <Box className="ua-step-content">
      <Typography variant="h6" className="ua-step-title">
        Album Details
      </Typography>

      <Stack direction={{ xs: "column", sm: "row" }} spacing={3}>
        {/* Cover Art */}
        <Box className="ua-cover-upload">
          <input
            type="file"
            accept="image/*"
            id="album-cover-input"
            hidden
            onChange={handleCoverChange}
          />
          <label htmlFor="album-cover-input" className="ua-cover-label">
            {coverPreview ? (
              <img src={coverPreview} alt="Cover" className="ua-cover-preview" />
            ) : (
              <Box className="ua-cover-placeholder">
                <UploadIcon sx={{ fontSize: 40, color: "#444" }} />
                <Typography variant="body2" sx={{ color: "#666", mt: 1 }}>
                  Upload Cover Art
                </Typography>
              </Box>
            )}
          </label>
        </Box>

        {/* Details */}
        <Stack spacing={2} sx={{ flex: 1 }}>
          <TextField
            label="Album Title"
            fullWidth
            required
            value={albumData.title}
            onChange={(e) =>
              setAlbumData({ ...albumData, title: e.target.value })
            }
            variant="outlined"
            InputProps={{ className: "ua-input" }}
            InputLabelProps={{ className: "ua-label" }}
          />

          <Stack direction="row" spacing={2}>
            <Box sx={{ flex: 1 }}>
              <DropDown
                onChange={(val) =>
                  setAlbumData({ ...albumData, genre: val })
                }
                title={albumData.genre || "Select Genre"}
                values={genres}
              />
            </Box>
            <Box sx={{ flex: 1 }}>
              <DropDown
                onChange={(val) =>
                  setAlbumData({ ...albumData, subGenre: val })
                }
                title={albumData.subGenre || "Select Sub-Genre"}
                values={genres}
              />
            </Box>
          </Stack>

          <TextField
            label="Release Date"
            type="date"
            fullWidth
            required
            value={albumData.releaseDate}
            onChange={(e) =>
              setAlbumData({ ...albumData, releaseDate: e.target.value })
            }
            InputLabelProps={{ shrink: true }}
            inputProps={{ max: new Date().toISOString().split("T")[0] }}
            InputProps={{ className: "ua-input" }}
          />

          <TextField
            label="Description"
            fullWidth
            required
            multiline
            rows={3}
            value={albumData.description}
            onChange={(e) =>
              setAlbumData({ ...albumData, description: e.target.value })
            }
            InputProps={{ className: "ua-input" }}
            InputLabelProps={{ className: "ua-label" }}
          />

          <FormControlLabel
            control={
              <Switch
                checked={albumData.isExplicit}
                onChange={(e) =>
                  setAlbumData({ ...albumData, isExplicit: e.target.checked })
                }
                sx={{
                  "& .MuiSwitch-switchBase.Mui-checked": {
                    color: "#FFEE20",
                  },
                  "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                    backgroundColor: "#FFEE20",
                  },
                }}
              />
            }
            label="Explicit Content"
            sx={{ color: "#888" }}
          />
        </Stack>
      </Stack>
    </Box>
  );

  const renderUploadTracks = () => (
    <Box className="ua-step-content">
      <Typography variant="h6" className="ua-step-title">
        Upload Tracks
      </Typography>

      {/* Drop zone */}
      <Box
        className={`ua-dropzone${dragOver ? " ua-dropzone--active" : ""}`}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
      >
        <input
          type="file"
          accept="audio/*,.mp3,.wav,.m4a,.flac"
          multiple
          id="tracks-input"
          hidden
          onChange={handleTracksChange}
        />
        <label htmlFor="tracks-input" className="ua-dropzone-label">
          <MusicIcon sx={{ fontSize: 48, color: dragOver ? "#FFEE20" : "#444" }} />
          <Typography variant="body1" sx={{ color: "#888", mt: 1 }}>
            Drag & drop audio files here, or click to browse
          </Typography>
          <Typography variant="body2" sx={{ color: "#555", mt: 0.5 }}>
            MP3, WAV, M4A, FLAC
          </Typography>
        </label>
      </Box>

      {/* Track list */}
      {tracks.length > 0 && (
        <Box className="ua-track-list">
          <Typography variant="body2" sx={{ color: "#888", mb: 1 }}>
            {tracks.length} track{tracks.length !== 1 ? "s" : ""} added
          </Typography>
          {tracks.map((track, index) => (
            <Box key={track.id} className="ua-track-item">
              <Typography className="ua-track-number">{index + 1}</Typography>
              <MusicIcon sx={{ color: "#FFEE20", fontSize: 20 }} />
              <TextField
                value={track.name}
                onChange={(e) => renameTrack(track.id, e.target.value)}
                variant="standard"
                sx={{ flex: 1, mx: 1 }}
                InputProps={{
                  disableUnderline: true,
                  className: "ua-track-name-input",
                }}
              />
              <Typography variant="body2" sx={{ color: "#555", mr: 1 }}>
                {formatFileSize(track.size)}
              </Typography>
              <IconButton
                size="small"
                onClick={() => moveTrack(index, -1)}
                disabled={index === 0}
                sx={{ color: "#666" }}
              >
                <ArrowUpIcon fontSize="small" />
              </IconButton>
              <IconButton
                size="small"
                onClick={() => moveTrack(index, 1)}
                disabled={index === tracks.length - 1}
                sx={{ color: "#666" }}
              >
                <ArrowDownIcon fontSize="small" />
              </IconButton>
              <IconButton
                size="small"
                onClick={() => removeTrack(track.id)}
                sx={{ color: "#666", "&:hover": { color: "#f44336" } }}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );

  const renderRoyaltyEditor = (royaltyObj, setRoyaltyFn) => {
    const helpers = updateRoyalty(royaltyObj, setRoyaltyFn);
    const total = helpers.total();

    return (
      <>
        <Box className="ua-royalty-row">
          <Typography sx={{ color: "#fff", flex: 1 }}>You (Owner)</Typography>
          <TextField
            type="number"
            value={royaltyObj.ownerRoyalty}
            onChange={(e) => helpers.setOwner(e.target.value)}
            inputProps={{ min: 0, max: 100 }}
            sx={{ width: 80 }}
            InputProps={{ className: "ua-input", endAdornment: <Typography sx={{ color: "#666" }}>%</Typography> }}
          />
        </Box>

        {royaltyObj.collaborators.map((collab, index) => (
          <Box key={index} className="ua-royalty-row">
            <TextField
              placeholder="Collaborator name"
              value={collab.name}
              onChange={(e) => helpers.updateCollab(index, "name", e.target.value)}
              sx={{ flex: 1, mr: 2 }}
              InputProps={{ className: "ua-input" }}
            />
            <TextField
              type="number"
              value={collab.percentage}
              onChange={(e) =>
                helpers.updateCollab(index, "percentage", Math.min(100, Math.max(0, Number(e.target.value))))
              }
              inputProps={{ min: 0, max: 100 }}
              sx={{ width: 80, mr: 1 }}
              InputProps={{ className: "ua-input", endAdornment: <Typography sx={{ color: "#666" }}>%</Typography> }}
            />
            <IconButton
              onClick={() => helpers.removeCollab(index)}
              sx={{ color: "#666", "&:hover": { color: "#f44336" } }}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Box>
        ))}

        <Button
          onClick={helpers.addCollab}
          sx={{
            color: "#FFEE20",
            textTransform: "none",
            mt: 1,
            "&:hover": { backgroundColor: "rgba(255, 238, 32, 0.08)" },
          }}
        >
          + Add Collaborator
        </Button>

        <Box sx={{ mt: 2, p: 2, borderRadius: "8px", background: "rgba(255,255,255,0.03)" }}>
          <Typography
            sx={{ color: total > 100 ? "#f44336" : "#888", fontWeight: 600 }}
          >
            Total: {total}%{total > 100 && " — exceeds 100%"}
          </Typography>
        </Box>
      </>
    );
  };

  const renderRoyalties = () => (
    <Box className="ua-step-content">
      <Typography variant="h6" className="ua-step-title">
        Royalty Splits
      </Typography>

      <FormControlLabel
        control={
          <Switch
            checked={usePerTrackSplits}
            onChange={(e) => handleTogglePerTrack(e.target.checked)}
            sx={{
              "& .MuiSwitch-switchBase.Mui-checked": { color: "#FFEE20" },
              "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": { backgroundColor: "#FFEE20" },
            }}
          />
        }
        label="Different splits per track"
        sx={{ color: "#888", mb: 2 }}
      />

      {!usePerTrackSplits ? (
        <>
          <Typography variant="body2" sx={{ color: "#666", mb: 2 }}>
            These splits apply to all tracks in the album.
          </Typography>
          {renderRoyaltyEditor(defaultRoyalty, setDefaultRoyalty)}
        </>
      ) : (
        <>
          {/* Track selector tabs */}
          <Box className="ua-track-tabs">
            {tracks.map((track, index) => (
              <Button
                key={track.id}
                onClick={() => setSelectedTrackId(track.id)}
                sx={{
                  textTransform: "none",
                  color: selectedTrackId === track.id ? "#FFEE20" : "#666",
                  borderBottom: selectedTrackId === track.id ? "2px solid #FFEE20" : "2px solid transparent",
                  borderRadius: 0,
                  px: 2,
                  py: 1,
                  minWidth: "auto",
                  fontSize: 13,
                  "&:hover": { color: "#ccc" },
                }}
              >
                {index + 1}. {track.name.length > 20 ? track.name.slice(0, 20) + "..." : track.name}
              </Button>
            ))}
          </Box>

          {selectedTrackId && trackRoyalties[selectedTrackId] && (
            <Box sx={{ mt: 2 }}>
              {renderRoyaltyEditor(
                trackRoyalties[selectedTrackId],
                (updated) => setTrackRoyalty(selectedTrackId, updated)
              )}
            </Box>
          )}
        </>
      )}
    </Box>
  );

  const renderReview = () => (
    <Box className="ua-step-content">
      <Typography variant="h6" className="ua-step-title">
        Review & Submit
      </Typography>

      {loading ? (
        <Box className="ua-upload-progress">
          <Typography variant="body1" sx={{ color: "#fff", mb: 2 }}>
            Uploading album...
          </Typography>
          <LinearProgress
            variant="determinate"
            value={uploadProgress}
            sx={{
              height: 8,
              borderRadius: 4,
              mb: 2,
              backgroundColor: "rgba(255,255,255,0.06)",
              "& .MuiLinearProgress-bar": { backgroundColor: "#FFEE20" },
            }}
          />
          <Typography variant="body2" sx={{ color: "#888", mb: 3 }}>
            {Math.round(uploadProgress)}% complete
          </Typography>

          {tracks.map((track, index) => (
            <Box key={track.id} className="ua-track-status">
              <Typography className="ua-track-number">{index + 1}</Typography>
              <Typography sx={{ color: "#ccc", flex: 1 }}>
                {track.name}
              </Typography>
              {trackStatuses[track.id]?.status === "uploading" && (
                <Typography sx={{ color: "#FFEE20", fontSize: 13 }}>
                  Uploading...
                </Typography>
              )}
              {trackStatuses[track.id]?.status === "success" && (
                <CheckIcon sx={{ color: "#4caf50", fontSize: 20 }} />
              )}
              {trackStatuses[track.id]?.status === "error" && (
                <ErrorIcon sx={{ color: "#f44336", fontSize: 20 }} />
              )}
            </Box>
          ))}
        </Box>
      ) : (
        <Box>
          {/* Album summary */}
          <Stack direction="row" spacing={3} sx={{ mb: 3 }}>
            {coverPreview && (
              <img
                src={coverPreview}
                alt="Cover"
                style={{
                  width: 120,
                  height: 120,
                  borderRadius: 12,
                  objectFit: "cover",
                }}
              />
            )}
            <Box>
              <Typography variant="h5" sx={{ color: "#fff", fontWeight: 700 }}>
                {albumData.title}
              </Typography>
              <Typography variant="body2" sx={{ color: "#888", mt: 0.5 }}>
                {albumData.genre}
                {albumData.subGenre ? ` / ${albumData.subGenre}` : ""}
              </Typography>
              <Typography variant="body2" sx={{ color: "#888" }}>
                {albumData.releaseDate}
              </Typography>
              <Typography variant="body2" sx={{ color: "#888" }}>
                {tracks.length} track{tracks.length !== 1 ? "s" : ""}
                {albumData.isExplicit ? " · Explicit" : ""}
              </Typography>
            </Box>
          </Stack>

          {/* Track list */}
          <Typography variant="body2" sx={{ color: "#666", mb: 1, textTransform: "uppercase", letterSpacing: 1 }}>
            Tracklist
          </Typography>
          {tracks.map((track, index) => (
            <Box key={track.id} className="ua-review-track">
              <Typography className="ua-track-number">{index + 1}</Typography>
              <TextField
                value={track.name}
                onChange={(e) => renameTrack(track.id, e.target.value)}
                variant="standard"
                sx={{ flex: 1 }}
                InputProps={{
                  disableUnderline: true,
                  className: "ua-track-name-input",
                  sx: { color: "#ccc" },
                }}
              />
            </Box>
          ))}

          {/* Royalties */}
          <Typography
            variant="body2"
            sx={{
              color: "#666",
              mb: 1,
              mt: 3,
              textTransform: "uppercase",
              letterSpacing: 1,
            }}
          >
            Royalty Splits{usePerTrackSplits ? " (per track)" : ""}
          </Typography>
          {!usePerTrackSplits ? (
            <>
              <Typography variant="body2" sx={{ color: "#ccc" }}>
                Owner: {defaultRoyalty.ownerRoyalty}%
              </Typography>
              {defaultRoyalty.collaborators.map((c, i) => (
                <Typography key={i} variant="body2" sx={{ color: "#ccc" }}>
                  {c.name || "Unnamed"}: {c.percentage}%
                </Typography>
              ))}
            </>
          ) : (
            tracks.map((track, index) => {
              const r = getRoyaltyForTrack(track.id);
              return (
                <Box key={track.id} sx={{ mb: 1 }}>
                  <Typography variant="body2" sx={{ color: "#FFEE20", fontSize: 12 }}>
                    {index + 1}. {track.name}
                  </Typography>
                  <Typography variant="body2" sx={{ color: "#ccc", pl: 2 }}>
                    Owner: {r.ownerRoyalty}%
                    {r.collaborators.map((c, ci) => (
                      <span key={ci}> · {c.name || "Unnamed"}: {c.percentage}%</span>
                    ))}
                  </Typography>
                </Box>
              );
            })
          )}
        </Box>
      )}
    </Box>
  );

  return (
    <Box className="ua-container">
      <Box className="ua-header">
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Typography variant="h5" sx={{ color: "#fff", fontWeight: 700 }}>
            Upload Album
          </Typography>
          {uploadLimits && (
            <Stack direction="row" alignItems="center" spacing={1.5}>
              <Box
                sx={{
                  px: 1.5,
                  py: 0.5,
                  borderRadius: "6px",
                  backgroundColor: uploadLimits.tier === "pro" ? "rgba(255, 238, 32, 0.15)" : "rgba(255,255,255,0.06)",
                  border: `1px solid ${uploadLimits.tier === "pro" ? "rgba(255, 238, 32, 0.3)" : "rgba(255,255,255,0.1)"}`,
                }}
              >
                <Typography sx={{ color: uploadLimits.tier === "pro" ? "#FFEE20" : "#888", fontSize: 12, fontWeight: 600, textTransform: "uppercase" }}>
                  {uploadLimits.tier === "free" ? "Free" : uploadLimits.tier === "basic" ? "Basic" : "Pro"}
                </Typography>
              </Box>
              <Typography sx={{ color: uploadLimits.remaining === 0 ? "#f44336" : "#888", fontSize: 13 }}>
                {uploadLimits.remaining === -1
                  ? "Unlimited uploads"
                  : `${uploadLimits.remaining} of ${uploadLimits.limit} uploads remaining`}
              </Typography>
            </Stack>
          )}
        </Stack>
      </Box>

      <Stepper
        activeStep={activeStep}
        alternativeLabel
        className="ua-stepper"
        sx={{
          mb: 4,
          "& .MuiStepLabel-label": { color: "#555", fontSize: 13 },
          "& .MuiStepLabel-label.Mui-active": { color: "#FFEE20" },
          "& .MuiStepLabel-label.Mui-completed": { color: "#FFEE20" },
          "& .MuiStepIcon-root": { color: "#333" },
          "& .MuiStepIcon-root.Mui-active": { color: "#FFEE20" },
          "& .MuiStepIcon-root.Mui-completed": { color: "#FFEE20" },
          "& .MuiStepConnector-line": {
            borderColor: "rgba(255,255,255,0.06)",
          },
        }}
      >
        {steps.map((label) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>

      {renderStep()}

      {/* Rights Confirmation - shown on final step */}
      {activeStep === steps.length - 1 && (
        <Stack direction="row" alignItems="flex-start" sx={{ mt: 3 }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={rightsConfirmed}
                onChange={(e) => setRightsConfirmed(e.target.checked)}
                sx={{ color: '#FFEE20', '&.Mui-checked': { color: '#FFEE20' } }}
              />
            }
            label={
              <Typography sx={{ fontSize: '13px', color: '#aaa' }}>
                By uploading, I confirm that I own or have the necessary rights and licenses to distribute this content. I accept full responsibility for any copyright claims related to this upload.
              </Typography>
            }
          />
        </Stack>
      )}

      {/* Navigation buttons */}
      {!loading && (
        <Stack
          direction="row"
          justifyContent="space-between"
          sx={{ mt: 4 }}
        >
          <Button
            onClick={activeStep === 0 ? () => history.goBack() : handleBack}
            sx={{
              color: "#888",
              textTransform: "none",
              "&:hover": { backgroundColor: "rgba(255,255,255,0.04)" },
            }}
          >
            {activeStep === 0 ? "Cancel" : "Back"}
          </Button>

          {activeStep < steps.length - 1 ? (
            <Button
              onClick={handleNext}
              variant="contained"
              sx={{
                backgroundColor: "#FFEE20",
                color: "#000",
                textTransform: "none",
                fontWeight: 600,
                px: 4,
                "&:hover": { backgroundColor: "#FFE066" },
              }}
            >
              Next
            </Button>
          ) : (
            <Button
              onClick={handleSubmit}
              variant="contained"
              disabled={loading}
              sx={{
                backgroundColor: "#FFEE20",
                color: "#000",
                textTransform: "none",
                fontWeight: 600,
                px: 4,
                "&:hover": { backgroundColor: "#FFE066" },
              }}
            >
              Upload Album
            </Button>
          )}
        </Stack>
      )}
    </Box>
  );
}
